/**
 * Pure validator for the workspace lifecycle retention policy form.
 *
 * The matching backend rules live in
 * ``rest_handlers._WORKSPACE_SETTINGS_FIELDS`` +
 * ``update_workspace_settings_handler``. Keeping a parallel pure
 * validator on the FE means the Save button stays disabled with a
 * useful tooltip *before* we round-trip a 400, which keeps the
 * server's audit log clean.
 *
 * Server-enforced rules mirrored here:
 *
 *   * Each numeric field is an integer.
 *   * 1 ≤ inactiveNotifyDays ≤ 365.
 *   * 1 ≤ inactiveRetireDays ≤ 365.
 *   * 1 ≤ archiveAfterDays   ≤ 365.
 *   * inactiveNotifyDays < inactiveRetireDays.
 *
 * Returns ``{ ok: true, value }`` with normalized integer values when
 * the form is acceptable; ``{ ok: false, error, field }`` otherwise.
 * ``field`` is the offending input id so the form can highlight it.
 */

const FIELD_DEFS = Object.freeze({
  inactiveNotifyDays: {
    label: 'Inactivity notification window',
    min: 1,
    max: 365,
  },
  inactiveRetireDays: {
    label: 'Auto-retire window',
    min: 1,
    max: 365,
  },
  archiveAfterDays: {
    label: 'Auto-archive window',
    min: 1,
    max: 365,
  },
});

export const RETENTION_POLICY_FIELDS = Object.freeze(Object.keys(FIELD_DEFS));
export const RETENTION_POLICY_DEFS = FIELD_DEFS;

/**
 * SNAPSHOT retention (LIFECYCLE.md §3) — the same Retention tab; enforced by the snapshot handlers
 * (``legacy._snapshot_retention``; before L154 they were stored but ignored). Validated only when present in the draft
 * (older callers send room fields only). Bounds mirror ``_WORKSPACE_SETTINGS_FIELDS``.
 */
const SNAPSHOT_DEFS = Object.freeze({
  snapshotUserKeep: { label: 'Saved snapshots kept', min: 1, max: 100, unit: 'snapshots' },
  autoCheckpointKeep: { label: 'Automatic checkpoints kept', min: 1, max: 100, unit: 'checkpoints' },
  userSnapshotTtlDays: { label: 'Snapshot expiry', min: 1, max: 3650, unit: 'days' },
});
export const SNAPSHOT_RETENTION_FIELDS = Object.freeze(Object.keys(SNAPSHOT_DEFS));
export const SNAPSHOT_RETENTION_DEFS = SNAPSHOT_DEFS;

/**
 * Coerce a free-form input value (string from <input type="number">,
 * or a JSON number / null) to an integer or ``null``. ``null`` means
 * "couldn't parse" — the caller treats that as "leave unchanged".
 */
export function coerceRetentionInt(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') {
    return Number.isFinite(raw) && Math.trunc(raw) === raw ? raw : null;
  }
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (!/^-?\d+$/.test(trimmed)) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * @param {{
 *   inactiveNotifyDays?: any,
 *   inactiveRetireDays?: any,
 *   archiveAfterDays?: any,
 * }} draft
 * @returns {
 *   | { ok: true, value: { inactiveNotifyDays: number, inactiveRetireDays: number, archiveAfterDays: number } }
 *   | { ok: false, error: string, field: string }
 * }
 */
export function validateRetentionPolicy(draft) {
  const out = {};
  for (const field of RETENTION_POLICY_FIELDS) {
    const def = FIELD_DEFS[field];
    const n = coerceRetentionInt(draft ? draft[field] : null);
    if (n === null) {
      return {
        ok: false,
        field,
        error: `${def.label} must be a whole number.`,
      };
    }
    if (n < def.min || n > def.max) {
      return {
        ok: false,
        field,
        error: `${def.label} must be between ${def.min} and ${def.max} days.`,
      };
    }
    out[field] = n;
  }
  for (const field of SNAPSHOT_RETENTION_FIELDS) {
    const raw = draft ? draft[field] : undefined;
    if (raw === undefined) continue;
    const def = SNAPSHOT_DEFS[field];
    const n = coerceRetentionInt(raw);
    if (n === null) return { ok: false, field, error: `${def.label} must be a whole number.` };
    if (n < def.min || n > def.max) {
      return { ok: false, field, error: `${def.label} must be between ${def.min} and ${def.max} ${def.unit}.` };
    }
    out[field] = n;
  }
  if (out.inactiveNotifyDays >= out.inactiveRetireDays) {
    return {
      ok: false,
      field: 'inactiveNotifyDays',
      error:
        'Notification window must be strictly less than the auto-retire '
        + 'window — otherwise the warning lane is skipped and pools jump '
        + 'straight from Active to Retired.',
    };
  }
  return { ok: true, value: out };
}

/**
 * Build the diff body for ``putWorkspaceSettings``: only fields that
 * actually changed are forwarded. Avoids spurious ``updatedAt`` bumps
 * (and audit trail churn) when the operator opens the page, scrolls,
 * and clicks Save without touching anything.
 *
 * @param {{ inactiveNotifyDays: number, inactiveRetireDays: number, archiveAfterDays: number }} next
 * @param {{ inactiveNotifyDays?: number, inactiveRetireDays?: number, archiveAfterDays?: number } | null} current
 */
export function diffRetentionPolicy(next, current) {
  const diff = {};
  if (!next) return diff;
  for (const field of [...RETENTION_POLICY_FIELDS, ...SNAPSHOT_RETENTION_FIELDS]) {
    if (next[field] === undefined) continue;
    const a = next[field];
    const b = current ? current[field] : undefined;
    if (a !== b) diff[field] = a;
  }
  return diff;
}
